import React, { useState, useRef, useCallback } from 'react';

/**
 * BeforeAfterSlider — Genially-style interactive image comparison slider.
 *
 * Features:
 *   ✦ Smooth drag & touch slide
 *   ✦ Gradient glow divider line
 *   ✦ Animated pulsing handle on first load (hint to interact)
 *   ✦ Hover expansion of the handle
 *   ✦ Pointer movement updates the comparison while pressed
 *   ✦ Floating labels that follow the divider
 *   ✦ "Press and drag" hint that disappears after first interaction
 *
 * The hint text comes in via the `dragHint` prop. It used to be derived by
 * string-comparing `beforeLabel === 'До'`, which silently fell back to English
 * the moment that translation string was edited.
 */
const clampPercent = (value) => Math.max(0, Math.min(100, value));
const COMPARE_IMAGE_WIDTH = 1200;
const COMPARE_IMAGE_HEIGHT = 900;
// Geometry stays in Tailwind classes: react-snap's old Chromium drops
// `aspect-ratio` / `inset` from inline styles, breaking hydration.
// Only dynamic values (cursor, clip-path, handle offset) are inline.

// Horizontal-movement threshold (px) before we capture the pointer and
// commit to scrubbing. This prevents the slider from hijacking vertical
// scroll gestures on touch devices.
const DRAG_THRESHOLD = 6;

export const BeforeAfterSlider = ({
    beforeImage,
    afterImage,
    beforePreviewImage,
    afterPreviewImage,
    beforeLabel = 'До',
    afterLabel = 'Після',
    dragHint = 'Затисніть і проведіть',
    compact = false,
    loading = 'lazy',
    className = '',
}) => {
    const [position, setPosition] = useState(50);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [isPointerActive, setIsPointerActive] = useState(false);
    const containerRef = useRef(null);
    const isPointerActiveRef = useRef(false);
    // Track the initial pointer-down coordinates for threshold detection
    const pointerOriginRef = useRef(null);
    const isCapturedRef = useRef(false);

    const getPercent = useCallback((clientX) => {
        if (!containerRef.current) return 50;
        const rect = containerRef.current.getBoundingClientRect();
        // Guard against 0-width (hidden / not-yet-laid-out container) — avoids NaN.
        if (!rect.width) return 50;
        return clampPercent(((clientX - rect.left) / rect.width) * 100);
    }, []);

    const markInteracted = useCallback(() => {
        if (!hasInteracted) setHasInteracted(true);
    }, [hasInteracted]);

    const updateFromPointer = useCallback((clientX) => {
        markInteracted();
        setPosition(getPercent(clientX));
    }, [getPercent, markInteracted]);

    const handlePointerDown = useCallback((event) => {
        isPointerActiveRef.current = true;
        pointerOriginRef.current = { x: event.clientX, y: event.clientY };
        isCapturedRef.current = false;
        setIsPointerActive(true);
        // Don't capture immediately — wait until horizontal threshold is exceeded
    }, []);

    const handlePointerMove = useCallback((event) => {
        if (!isPointerActiveRef.current) return;

        // If not yet captured, check if we exceeded horizontal threshold
        if (!isCapturedRef.current) {
            const origin = pointerOriginRef.current;
            if (!origin) return;
            const dx = Math.abs(event.clientX - origin.x);
            const dy = Math.abs(event.clientY - origin.y);
            // If vertical movement dominates, release — let the page scroll
            if (dy > DRAG_THRESHOLD && dy > dx) {
                isPointerActiveRef.current = false;
                setIsPointerActive(false);
                pointerOriginRef.current = null;
                return;
            }
            // Horizontal movement exceeds threshold → capture and start scrubbing
            if (dx > DRAG_THRESHOLD) {
                isCapturedRef.current = true;
                event.currentTarget.setPointerCapture?.(event.pointerId);
                updateFromPointer(event.clientX);
            }
            return;
        }

        updateFromPointer(event.clientX);
    }, [updateFromPointer]);

    const handlePointerEnd = useCallback((event) => {
        // If we never captured (tap without drag), treat as a position jump
        if (isPointerActiveRef.current && !isCapturedRef.current) {
            updateFromPointer(event.clientX);
        }
        isPointerActiveRef.current = false;
        isCapturedRef.current = false;
        pointerOriginRef.current = null;
        setIsPointerActive(false);
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    }, [updateFromPointer]);

    const handleKeyDown = useCallback((event) => {
        const step = event.shiftKey ? 10 : 5;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            markInteracted();
            setPosition(prev => clampPercent(prev - step));
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            markInteracted();
            setPosition(prev => clampPercent(prev + step));
        } else if (event.key === 'Home') {
            event.preventDefault();
            markInteracted();
            setPosition(0);
        } else if (event.key === 'End') {
            event.preventDefault();
            markInteracted();
            setPosition(100);
        }
    }, [markInteracted]);

    const handleSize = compact ? 'w-9 h-9 sm:w-11 sm:h-11' : 'w-11 h-11 sm:w-14 sm:h-14';
    const iconSize = compact ? 'w-4 h-4 sm:w-5 sm:h-5' : 'w-5 h-5 sm:w-6 sm:h-6';
    const pulseSize = compact ? 'w-11 h-11 sm:w-12 sm:h-12' : 'w-14 h-14 sm:w-16 sm:h-16';
    const labelClassName = compact
        ? 'inline-flex items-center px-2 py-0.5 bg-black/50 backdrop-blur-md text-white text-[10px] sm:text-xs font-bold rounded-full tracking-wide uppercase shadow-lg'
        : 'inline-flex items-center px-3.5 py-1.5 bg-black/50 backdrop-blur-md text-white text-xs sm:text-sm font-bold rounded-lg tracking-wide uppercase shadow-lg';

    const rounded = Math.round(position);

    return (
        <div
            ref={containerRef}
            className={`relative w-full aspect-[4/3] overflow-hidden rounded-2xl select-none bg-gray-100 group touch-pan-y focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:outline-none ${className}`}
            style={{
                cursor: isPointerActive ? 'grabbing' : 'grab',
                touchAction: 'pan-y',
            }}
            role="slider"
            tabIndex={0}
            aria-label={`${beforeLabel} / ${afterLabel}`}
            aria-orientation="horizontal"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={rounded}
            aria-valuetext={`${beforeLabel} ${100 - rounded}%, ${afterLabel} ${rounded}%`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onKeyDown={handleKeyDown}
        >
            {/* === AFTER image (full, bottom layer) === */}
            <img
                src={afterPreviewImage || afterImage}
                alt={afterLabel}
                className="absolute inset-0 w-full h-full object-cover"
                width={COMPARE_IMAGE_WIDTH}
                height={COMPARE_IMAGE_HEIGHT}
                draggable={false}
                loading={loading}
                decoding="async"
            />

            {/* === BEFORE image (clipped, top layer) === */}
            <div
                className="absolute inset-0 will-change-[clip-path]"
                style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
            >
                <img
                    src={beforePreviewImage || beforeImage}
                    alt={beforeLabel}
                    className="absolute inset-0 w-full h-full object-cover"
                    width={COMPARE_IMAGE_WIDTH}
                    height={COMPARE_IMAGE_HEIGHT}
                    draggable={false}
                    loading={loading}
                    decoding="async"
                />
            </div>

            {/* === Divider line with gradient glow === */}
            <div
                className="absolute top-0 bottom-0 z-10 pointer-events-none"
                style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
            >
                {/* Glow */}
                <div className="absolute inset-0 w-8 -translate-x-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                {/* Line */}
                <div className="absolute inset-0 w-[3px] -translate-x-1/2 bg-white shadow-[0_0_12px_rgba(255,255,255,0.5)]" />
            </div>

            {/* === Drag handle === */}
            <div
                className="absolute top-1/2 z-20"
                style={{ left: `${position}%`, transform: 'translate(-50%, -50%)' }}
            >
                {/* Pulse ring — shows only before first interaction */}
                {!hasInteracted && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className={`${pulseSize} rounded-full border-2 border-white/60 animate-ping`} />
                    </div>
                )}

                {/* Handle button */}
                <div
                    className={`
                        relative ${handleSize} rounded-full
                        bg-white/95 backdrop-blur-sm
                        shadow-[0_2px_20px_rgba(0,0,0,0.25)]
                        border-[3px] border-white
                        flex items-center justify-center
                        transition-transform duration-200 ease-out
                        ${isPointerActive ? 'scale-110' : 'group-hover:scale-110'}
                    `}
                    style={{ cursor: isPointerActive ? 'grabbing' : 'grab' }}
                >
                    <svg
                        className={`${iconSize} text-gold`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M7 4L3 12L7 20" />
                        <path d="M17 4L21 12L17 20" />
                    </svg>
                </div>
            </div>

            {/* === Labels === */}
            {/* Before label — floats near the divider, left side */}
            <div
                className="absolute z-10 transition-all duration-300"
                style={{
                    top: '1rem',
                    left: `${Math.min(position - 2, 50)}%`,
                    transform: 'translateX(-100%)',
                    opacity: position < 12 ? 0 : 1,
                }}
            >
                <span className={labelClassName}>
                    {beforeLabel}
                </span>
            </div>

            {/* After label — floats near the divider, right side */}
            <div
                className="absolute z-10 transition-all duration-300"
                style={{
                    top: '1rem',
                    left: `${Math.max(position + 2, 50)}%`,
                    opacity: position > 88 ? 0 : 1,
                }}
            >
                <span className={labelClassName}>
                    {afterLabel}
                </span>
            </div>

            {/* === "Press and drag" hint (first load only) === */}
            {!compact && !hasInteracted && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 animate-bounce">
                    <span className="inline-flex items-center space-x-1.5 px-4 py-2 bg-black/50 backdrop-blur-md text-white text-xs sm:text-sm font-medium rounded-full shadow-lg">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12H19M5 12L9 8M5 12L9 16M19 12L15 8M19 12L15 16" />
                        </svg>
                        <span>{dragHint}</span>
                    </span>
                </div>
            )}
        </div>
    );
};
